import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import TerminologyCard from '../cards/TerminologyCard'
import TerminologyList from '../lists/TerminologyList'
import { RESOURCE_TYPE_COMPONENTS } from '../resourceTypeComponents'

import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Loading from '../loading/Loading'
import IconButton from '@mui/material/IconButton'
import ReorderIcon from '@mui/icons-material/Reorder'
import AppsIcon from '@mui/icons-material/Apps'

import { initializeFavourites } from '../../reducers/favouritesReducer'
import { setErrorNotification } from '../../reducers/notificationReducer'
import Pagination from '@mui/material/Pagination'
import { PAGES } from '../navigation/routes'
import { PAGE_SIZE } from '../websiteConfig'
import { SearchStyle } from '../styles'

const FAVOURITE_TYPE_COMPONENTS = {
    ...RESOURCE_TYPE_COMPONENTS,
    Terminology: { Card: TerminologyCard, List: TerminologyList },
}

const Favourites = () => {
    const user = useSelector(state => state.user)
    const favourites = useSelector(state => state.favourites)
    const [list, setList] = useState(true)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const pageSize = PAGE_SIZE
    const [page, setPage] = useState(1)
    // The list can shrink under us (re-fetch, or the user unfavourites) while
    // on page 2+; clamp so the slice window is never empty (#641).
    const pageCount = Math.max(1, Math.ceil(favourites.length / pageSize))
    const currentPage = Math.min(page, pageCount)

    const renderFavourite = (resource) => {
        const type = resource.type
        const Component = FAVOURITE_TYPE_COMPONENTS[type]?.[list ? 'List' : 'Card']
        if (Component) return <Component {...resource} />
        console.warn(`Unrecognized resource type in renderFavourite: ${type} (id=${resource.id})`)
        return null
    }

    useEffect(() => {
        // state.user is null until the session resolves (userReducer initial
        // state); keep the spinner up rather than claiming there are no
        // favourites.
        if (!user) return

        if (user.anon) {
            dispatch(setErrorNotification('You must be logged in to view this page'))
            // Raise the spinner before redirecting: on logout the effect re-runs
            // with `loading` already false and the previous user's favourites
            // still in the slice, which would otherwise stay on screen until
            // navigate lands.
            setLoading(true)
            navigate(PAGES.home.path)
            // Without this return the redirected user ALSO fires the fetch.
            return
        }

        let isActive = true
        setLoading(true)
        // Clear a previous run's error up front. A failed fetch never dispatches
        // setFavourites, so on a retry that fails again the [favourites] effect
        // below does not fire — this reset is what clears it.
        setError(false)

        const load = async () => {
            const ok = await dispatch(initializeFavourites())
            // A superseded or unmounted run must not write state: `await` alone
            // does not stop an older promise resolving last.
            // (precedent: pages/Policies.js:101-121)
            if (!isActive) return
            // Only an explicit false is a failure.
            setError(ok === false)
            setLoading(false)
        }
        load()

        return () => {
            isActive = false
        }
    }, [user, dispatch, navigate])

    // The slice changing means we have fresh data (a failed fetch never
    // dispatches setFavourites), so a previous fetch error no longer describes
    // what is on screen — e.g. the user deleting their last favourite must show
    // the empty state, not a stale failure message.
    useEffect(() => {
        setError(false)
    }, [favourites])

    return (
        <Container sx={SearchStyle.container}>
            <Typography {...SearchStyle.title} gutterBottom>
                Favourites
            </Typography>
            <Box sx={SearchStyle.viewContainer}>
                <IconButton sx={{ color: list? '#002A5C' : '#CECFCB'}} onClick={() => setList(true)}>
                    <ReorderIcon />
                </IconButton>
                <IconButton sx={{ color: !list? '#002A5C' : '#CECFCB'}} onClick={() => setList(false)}>
                    <AppsIcon />
                </IconButton>
            </Box>
            {loading && <Loading />}
            {!loading && ((favourites.length > 0)?
                <>
                    <Grid container spacing={2}>
                        {favourites.slice((currentPage-1)*pageSize, currentPage*pageSize).map(favourite => {
                            const resource = favourite.favourite
                            return (
                                <Grid
                                    key={`${resource.type} ${resource.id}`}
                                    item
                                    xs={12}
                                    md={list ? 12 : 4}
                                    sx={list ? {} : SearchStyle.card}
                                >
                                    {renderFavourite(resource)}
                                </Grid>
                            )
                        })}
                    </Grid>
                    <Pagination sx={SearchStyle.pagination} count={pageCount} page={currentPage} onChange={(event, value) => setPage(value)} />
                </> :
                <Typography {...SearchStyle.noResources.props} gutterBottom sx={SearchStyle.noResources.style}>
                    {error ? "Couldn't load your favourites." : 'No Favourited Resources Found'}
                </Typography>)
            }
        </Container>
    )
}

export default Favourites